/**
 * ISSUE-977. The sortable Pinned section: a grip handle per row, mouse and
 * touch drag, the lifted row and the make-room animation while dragging, and
 * arrow key reorder on the focused handle.
 *
 * The grip handle is the only drag activator, so the checkbox and the
 * chevron never start a drag (R1). `touch-none` on the handle alone keeps a
 * touch drag from scrolling the sheet, while a swipe anywhere else on the
 * sheet still scrolls it (R4). Every drop and every key press saves through
 * `applyPinChange` (ISSUE-964). dnd-kit's own drag announcements read raw
 * league ids, so they are silenced; the polite live region below carries
 * every message instead.
 */
import {
  type Announcements,
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@sportsmet/ui";
import {
  type KeyboardEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { ICON_BUTTON_CLASS, LeagueRow } from "./LeagueMenuRow";
import {
  REORDER_HANDLE_INSTRUCTIONS,
  reorderAtEdgeAnnouncement,
  reorderHandleLabel,
  reorderMovedAnnouncement,
  silentDragAnnouncement,
  type typeLeagueSelectorOption,
} from "./leagueSelectorContract";
import {
  movePinnedLeagueTo,
  resolvePinnedLeagueDragEnd,
  resolvePinnedLeagueStep,
  togglePinnedLeague,
  type typePinnedLeagueMove,
} from "./pinnedLeagues";

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 4 } };

const SILENT_ANNOUNCEMENTS: Announcements = {
  onDragStart: silentDragAnnouncement,
  onDragOver: silentDragAnnouncement,
  onDragEnd: silentDragAnnouncement,
  onDragCancel: silentDragAnnouncement,
};

const DRAG_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];

const HANDLE_CLASS = cn(
  ICON_BUTTON_CLASS,
  "touch-none cursor-grab active:cursor-grabbing",
);

/** A six-dot grip, hidden from assistive tech: the handle button carries its
 *  own accessible name. */
const GripIcon = (): ReactNode => (
  <svg
    className="h-icon-md w-icon-md"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="7" cy="5" r="1.5" />
    <circle cx="13" cy="5" r="1.5" />
    <circle cx="7" cy="10" r="1.5" />
    <circle cx="13" cy="10" r="1.5" />
    <circle cx="7" cy="15" r="1.5" />
    <circle cx="13" cy="15" r="1.5" />
  </svg>
);

/** Under reduced motion the rows jump instead of sliding (`.agents/rules/ui-motion.mdc`). */
const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type typeSortablePinnedRowProps = {
  option: typeLeagueSelectorOption;
  onTogglePin: () => void;
  onOpen: () => void;
  onHandleKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

const SortablePinnedRow = (props: typeSortablePinnedRowProps): ReactNode => {
  const sortable = useSortable(
    prefersReducedMotion()
      ? { id: props.option.leagueId, transition: null }
      : { id: props.option.leagueId },
  );
  return (
    <LeagueRow
      option={props.option}
      isPinned={true}
      onTogglePin={props.onTogglePin}
      onOpen={props.onOpen}
      rowRef={sortable.setNodeRef}
      rowStyle={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
      }}
      isLifted={sortable.isDragging}
      reorder={
        <button
          type="button"
          ref={sortable.setActivatorNodeRef}
          data-testid="league-menu-pinned-row"
          aria-label={reorderHandleLabel(props.option.shortName)}
          aria-describedby={sortable.attributes["aria-describedby"]}
          className={HANDLE_CLASS}
          {...sortable.listeners}
          onKeyDown={props.onHandleKeyDown}
        >
          <GripIcon />
        </button>
      }
    />
  );
};

export type typePinnedLeagueListProps = {
  /** view.pinned from buildLeagueMenuView: the fan's pins the catalog returned, in stored order. */
  pinned: typeLeagueSelectorOption[];
  /** useLeaguePins().applyPinChange. */
  applyPinChange: (change: (current: string[]) => string[]) => void;
  /** Sets ?leagueId= and clears ?sheet=. */
  onSelectLeague: (leagueId: string) => void;
};

export const PinnedLeagueList = (
  props: typePinnedLeagueListProps,
): ReactNode => {
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));
  const [announcement, setAnnouncement] = useState("");
  const leagueIds = props.pinned.map((option) => option.leagueId);
  const listRef = useRef<HTMLUListElement>(null);
  const focusAfterMoveRef = useRef<string | null>(null);

  const commitMove = (move: typePinnedLeagueMove | null): void => {
    if (move === null) {
      return;
    }
    props.applyPinChange((current) =>
      movePinnedLeagueTo({
        pinnedLeagueIds: current,
        leagueId: move.leagueId,
        targetLeagueId: move.targetLeagueId,
      }),
    );
    const shortName =
      props.pinned.find((option) => option.leagueId === move.leagueId)
        ?.shortName ?? "";
    setAnnouncement(
      reorderMovedAnnouncement({
        shortName,
        position: move.position,
        total: leagueIds.length,
      }),
    );
  };

  // happy-dom lays nothing out, so no unit test can make dnd-kit call this;
  // everything it decides lives in the tested resolvePinnedLeagueDragEnd.
  const handleDragEnd = (event: DragEndEvent): void => {
    commitMove(
      resolvePinnedLeagueDragEnd({ visibleLeagueIds: leagueIds, event }),
    );
  };

  const handleHandleKeyDown = (
    option: typeLeagueSelectorOption,
    event: KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      return;
    }
    event.preventDefault();
    const move = resolvePinnedLeagueStep({
      visibleLeagueIds: leagueIds,
      leagueId: option.leagueId,
      direction: event.key === "ArrowUp" ? "up" : "down",
    });
    if (move === null) {
      setAnnouncement(
        reorderAtEdgeAnnouncement({
          shortName: option.shortName,
          position: leagueIds.indexOf(option.leagueId) + 1,
          total: leagueIds.length,
        }),
      );
      return;
    }
    focusAfterMoveRef.current = option.leagueId;
    commitMove(move);
  };

  const orderKey = leagueIds.join("|");
  // A keyboard move reorders the list, and a DOM node React moves can lose focus. Put it back on the moved league's handle (ISSUE-977).
  useLayoutEffect(() => {
    const id = focusAfterMoveRef.current;
    if (id === null) {
      return;
    }
    focusAfterMoveRef.current = null;
    const handle = listRef.current?.querySelector<HTMLButtonElement>(
      `li[data-league-id="${id}"] [data-testid="league-menu-pinned-row"]`,
    );
    if (
      handle !== null &&
      handle !== undefined &&
      document.activeElement !== handle
    ) {
      handle.focus();
    }
  }, [orderKey]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={DRAG_MODIFIERS}
        accessibility={{
          announcements: SILENT_ANNOUNCEMENTS,
          screenReaderInstructions: { draggable: REORDER_HANDLE_INSTRUCTIONS },
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={leagueIds}
          strategy={verticalListSortingStrategy}
        >
          <ul ref={listRef}>
            {props.pinned.map((option) => (
              <SortablePinnedRow
                key={option.leagueId}
                option={option}
                onTogglePin={() => {
                  props.applyPinChange((current) =>
                    togglePinnedLeague({
                      pinnedLeagueIds: current,
                      leagueId: option.leagueId,
                    }),
                  );
                }}
                onOpen={() => {
                  props.onSelectLeague(option.leagueId);
                }}
                onHandleKeyDown={(event) => {
                  handleHandleKeyDown(option, event);
                }}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="league-menu-reorder-status"
      >
        {announcement}
      </p>
    </>
  );
};
